import Box, { BoxProps } from '@liquidweb-dev/undercurrent/Box';
import { pseudoClasses } from '@liquidweb-dev/undercurrent/classes/pseudo';
import { dynamicBadgeClasses } from '@liquidweb-dev/undercurrent/DynamicBadge';
import ListItem, {
	type ListItemProps,
} from '@liquidweb-dev/undercurrent/ListItem';
import ListItemButton, {
	ListItemButtonProps,
} from '@liquidweb-dev/undercurrent/ListItemButton';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import { Link as RouterLink } from 'react-router-dom';

import { assetListItemMenuAreaClasses } from './classNames';

export const containerName = 'asset-list-item';
export const containerQueries = {
	0: `@0/${containerName}`,
	820: `@820/${containerName}`,
} as const;

export const gridStyles = {
	display: 'grid',
	gridTemplateColumns: {
		[containerQueries[0]]: '1fr auto',
		[containerQueries[820]]: '1fr auto auto',
	},
	gridTemplateAreas: {
		[containerQueries[0]]: `
					    'name .'
							'details details'
						`,
		[containerQueries[820]]: `
					    'name details details'
						`,
	},
	gap: 300,
	alignItems: 'center',

	// Automatically apply minHeight when AssetListItemStatus (DynamicBadge) is present
	[`&:has(.${dynamicBadgeClasses.root})`]: {
		// need to override minHeight of ListItemButton
		[pseudoClasses.important]: {
			minHeight: '64px', // size of status badge + vertical padding
		},
	},

	// Automatically apply correct grid template areas when AssetListItemMenuArea is present
	[`&:has(.${assetListItemMenuAreaClasses.root})`]: {
		gridTemplateAreas: {
			[containerQueries[0]]: `
					    'name menu'
							'details details'
						`,
			[containerQueries[820]]: `
					    'name details menu'
						`,
		},
	},
} as const;

export interface AssetListItemProps extends ListItemProps {
	innerBoxProps?: Omit<BoxProps, 'children'>;
	listItemButtonProps?: Omit<ListItemButtonProps, 'children'>;
	to?: string;
}

const AssetListItem = ({
	children,
	innerBoxProps,
	listItemButtonProps,
	to,
	sx,
	...rest
}: AssetListItemProps) => {
	const { sx: listItemButtonSx, ...restListItemButtonProps } =
		listItemButtonProps || {};
	const { sx: innerBoxSx, ...restInnerBoxProps } = innerBoxProps || {};

	return (
		<ListItem
			sx={combineSx(
				{ container: containerName, containerType: 'inline-size' },
				sx,
			)}
			{...rest}
		>
			{to ? (
				<ListItemButton
					component={RouterLink}
					sx={combineSx(gridStyles, listItemButtonSx)}
					to={to}
					{...restListItemButtonProps}
				>
					{children}
				</ListItemButton>
			) : (
				<Box
					sx={combineSx({ ...gridStyles, width: '100%' }, innerBoxSx)}
					{...restInnerBoxProps}
				>
					{children}
				</Box>
			)}
		</ListItem>
	);
};

export default AssetListItem;
